import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { pageTasksApi, pageTypesApi, settingsApi, pagesApi } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { StatusBadge, PriorityBadge, TeamBadge, OverdueBadge } from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Input, { Select, Textarea } from '../components/ui/Input';
import { FormattedNotes, NotesEditor } from '../components/ui/MarkdownNotes';
import { FollowUpOwnerSelect, MultiAssigneePicker } from '../components/ui/AssignmentFields';
import { TableSkeleton } from '../components/ui/Skeleton';
import {
  formatAssigneeNames,
  formatDate,
  getOverdueDays,
  getTeamsForAssignees,
  memberCanSeeTask,
  normalizeUrlForWebsite,
  parseAssigneeList,
  PRIORITIES,
  STATUSES,
  TEAMS,
} from '../lib/utils';

const TASK_TYPES = [
  'Meta Title', 'Meta Description', 'H1 Tag', 'Canonical Tag',
  'Image Alt Text', 'Internal Linking', 'Schema Markup',
  'Content Optimization', 'Dev Fix', 'Design Fix', 'Other',
];

const STATUS_FILTERS = [
  { value: 'open', label: 'Pending + In Progress' },
  { value: 'Pending', label: 'Pending' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Done', label: 'Done' },
  { value: 'Blocked', label: 'Blocked' },
  { value: 'all', label: 'All Statuses' },
];

// ── Searchable URL combobox ──────────────────────────────────────────────────
function UrlCombobox({ pageTypeId, websiteId, value, onChange }: {
  pageTypeId: number | null;
  websiteId: number | null;
  value: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value || '');
  const ref = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['pages', pageTypeId, websiteId, search],
    queryFn: () => pagesApi.list(pageTypeId!, search, websiteId),
    enabled: !!pageTypeId,
    staleTime: 60000,
  });

  const pages: { url: string }[] = data?.data || [];

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // sync external value
  useEffect(() => { setSearch(value || ''); }, [value]);

  const select = (url: string) => {
    onChange(url);
    setSearch(url);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">Page URL</label>
      <input
        type="text"
        value={search}
        onChange={e => { setSearch(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => pageTypeId && setOpen(true)}
        placeholder={pageTypeId ? 'Type to search or select URL...' : 'Type or paste URL (optional)'}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 disabled:bg-gray-50 disabled:text-gray-400"
      />
      {open && pageTypeId && (
        <div className="absolute right-0 z-50 mt-1 w-[min(680px,calc(100vw-2rem))] bg-white border border-gray-200 rounded-xl shadow-lg max-h-72 overflow-y-auto">
          {isLoading && (
            <div className="px-3 py-2 text-sm text-gray-400">Loading URLs...</div>
          )}
          {!isLoading && pages.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-400">No URLs found</div>
          )}
          {pages.map(p => (
            <button
              key={p.url}
              type="button"
              title={p.url}
              onMouseDown={() => select(p.url)}
              className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-orange-50 hover:text-orange-700 font-mono whitespace-normal break-all leading-relaxed"
            >
              {p.url}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Task Form ────────────────────────────────────────────────────────────────
function TaskForm({ defaultValues, onSubmit, loading, submitLabel, pageTypes, members, websites }: any) {
  const { user } = useAuth();
  const isMember = (user as any)?.role === 'member';
  const currentMemberName = ((user as any)?.team_member_name || user?.username || '').trim();
  const currentMember = members.find((m: any) =>
    currentMemberName && m.name.toLowerCase() === currentMemberName.toLowerCase()
  );
  const currentTeam = currentMember?.team || defaultValues?.assigned_team || '';
  const defaultWebsiteId = defaultValues?.website_id || websites[0]?.id || '';
  const initialTaskType = defaultValues?.task_type || '';
  const isKnownTaskType = !initialTaskType || TASK_TYPES.includes(initialTaskType);
  const initialAssignees = parseAssigneeList(
    defaultValues?.assigned_to_list,
    defaultValues?.assigned_to || (isMember ? currentMemberName : '')
  );
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>(initialAssignees);
  const [followUpBy, setFollowUpBy] = useState<string>(defaultValues?.follow_up_by || (isMember ? currentMemberName : ''));
  const [selectedPageTypeId, setSelectedPageTypeId] = useState<number | null>(
    defaultValues?.page_type_id ? Number(defaultValues.page_type_id) : null
  );

  const { register, handleSubmit, control, setValue, watch } = useForm({
    defaultValues: {
      page_type_id: defaultValues?.page_type_id ? String(defaultValues.page_type_id) : '',
      website_id: defaultWebsiteId ? String(defaultWebsiteId) : '',
      page_url: defaultValues?.page_url || '',
      task_type: isKnownTaskType ? initialTaskType : 'Other',
      custom_task_type: isKnownTaskType ? '' : initialTaskType,
      assigned_team: defaultValues?.assigned_team || (isMember ? currentTeam : ''),
      priority: defaultValues?.priority || 'Medium',
      status: defaultValues?.status || 'Pending',
      due_date: defaultValues?.due_date || '',
      notes: defaultValues?.notes || '',
      estimated_hours: defaultValues?.estimated_hours ?? 0,
      estimated_minutes: defaultValues?.estimated_minutes ?? 0,
    },
  });

  const HOURS = Array.from({ length: 100 }, (_, i) => i);
  const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  const websiteValue = watch('website_id');
  const selectedWebsiteId = websiteValue ? Number(websiteValue) : null;
  const selectedTaskType = watch('task_type');

  const handleFormSubmit = (data: any) => {
    if (!data.due_date) {
      toast.error('Due date is required');
      return;
    }

    const estimatedHours = parseInt(data.estimated_hours) || 0;
    const estimatedMinutes = parseInt(data.estimated_minutes) || 0;
    if (estimatedHours * 60 + estimatedMinutes <= 0) {
      toast.error('Time estimate is required');
      return;
    }
    const customTaskType = String(data.custom_task_type || '').trim();
    if (data.task_type === 'Other' && !customTaskType) {
      toast.error('Please enter custom task type');
      return;
    }

    onSubmit({
      ...data,
      task_type: data.task_type === 'Other' ? customTaskType : data.task_type,
      custom_task_type: undefined,
      website_id: data.website_id ? parseInt(data.website_id) : null,
      page_type_id: data.page_type_id ? parseInt(data.page_type_id) : null,
      assigned_to: selectedAssignees.join(', ') || null,
      assigned_to_list: selectedAssignees,
      assigned_team: getTeamsForAssignees(selectedAssignees, members, data.assigned_team || ''),
      follow_up_by: followUpBy || null,
      estimated_hours: estimatedHours,
      estimated_minutes: estimatedMinutes,
    });
  };

  const pageTypeOptions = [
    { value: '', label: 'Other / General' },
    ...pageTypes.map((p: any) => ({ value: String(p.id), label: p.name })),
  ];
  const websiteOptions = [
    { value: '', label: 'Select website' },
    ...websites.map((site: any) => ({ value: String(site.id), label: site.name })),
  ];
  const taskTypeOptions = [
    { value: '', label: 'Select task type' },
    ...TASK_TYPES.map(t => ({ value: t, label: t })),
  ];
  const teamOptions = [
    { value: '', label: 'Select team' },
    ...TEAMS.map(t => ({ value: t, label: t })),
  ];

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <Select
        label="Website *"
        {...register('website_id', { required: true })}
        onChange={event => {
          register('website_id').onChange(event);
          setValue('page_url', '');
        }}
        options={websiteOptions}
      />

      <div className="grid grid-cols-2 gap-4">
        <Select
          label="Page Type"
          {...register('page_type_id')}
          onChange={e => {
            register('page_type_id').onChange(e);
            setSelectedPageTypeId(e.target.value ? parseInt(e.target.value) : null);
            setValue('page_url', '');
          }}
          options={pageTypeOptions}
        />
        <Controller
          name="page_url"
          control={control}
          render={({ field }) => (
            <UrlCombobox
              pageTypeId={selectedPageTypeId}
              websiteId={selectedWebsiteId}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>

      <Select label="Task Type" {...register('task_type')} options={taskTypeOptions} />
      {selectedTaskType === 'Other' && (
        <Input
          label="Custom Task Type *"
          {...register('custom_task_type')}
          placeholder="e.g. Footer CTA update, Broken section fix"
        />
      )}

      <div className="grid grid-cols-2 gap-4">
        <Select label="Team" {...register('assigned_team')} options={teamOptions} />
        <FollowUpOwnerSelect members={members} value={followUpBy} onChange={setFollowUpBy} />
      </div>

      <MultiAssigneePicker
        label="Assigned To"
        members={members}
        value={selectedAssignees}
        onChange={assignees => {
          setSelectedAssignees(assignees);
          const team = getTeamsForAssignees(assignees, members);
          if (team) setValue('assigned_team', team);
        }}
      />

      <div className="grid grid-cols-2 gap-4">
        <Select label="Priority" {...register('priority')} options={PRIORITIES.map(p => ({ value: p, label: p }))} />
        <Select label="Status" {...register('status')} options={STATUSES.map(s => ({ value: s, label: s }))} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input label="Due Date *" type="date" {...register('due_date')} />
        <div>
          <label className="block text-sm font-semibold text-[#18181B] mb-1.5">Time Estimate *</label>
          <div className="flex gap-2">
            <select {...register('estimated_hours')}
              className="flex-1 border border-[#E4E4E7] rounded-lg px-3 py-2 text-sm text-[#18181B] focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#F97316] bg-white transition-all duration-150">
              {HOURS.map(h => <option key={h} value={h}>{h}h</option>)}
            </select>
            <select {...register('estimated_minutes')}
              className="flex-1 border border-[#E4E4E7] rounded-lg px-3 py-2 text-sm text-[#18181B] focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#F97316] bg-white transition-all duration-150">
              {MINUTES.map(m => <option key={m} value={m}>{m}m</option>)}
            </select>
          </div>
        </div>
      </div>

      <Controller
        name="notes"
        control={control}
        render={({ field }) => (
          <NotesEditor
            value={field.value}
            onChange={field.onChange}
            rows={3}
            placeholder="Any additional notes..."
          />
        )}
      />

      <div className="flex justify-end pt-2">
        <Button type="submit" loading={loading}>{submitLabel || 'Create Task'}</Button>
      </div>
    </form>
  );
}

// ── Batch Form ───────────────────────────────────────────────────────────────
function BatchForm({ onSubmit, loading, pageTypes, members, websites }: any) {
  const { user } = useAuth();
  const isMember = (user as any)?.role === 'member';
  const currentMemberName = ((user as any)?.team_member_name || user?.username || '').trim();
  const currentMember = members.find((m: any) =>
    currentMemberName && m.name.toLowerCase() === currentMemberName.toLowerCase()
  );
  const currentTeam = currentMember?.team || '';
  const HOURS = Array.from({ length: 100 }, (_, i) => i);
  const MINUTES = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
  const defaultWebsiteId = websites[0]?.id || '';
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>(isMember && currentMemberName ? [currentMemberName] : []);
  const [followUpBy, setFollowUpBy] = useState<string>(isMember ? currentMemberName : '');
  const { register, handleSubmit, setValue, watch } = useForm<any>({
    defaultValues: {
      website_id: defaultWebsiteId ? String(defaultWebsiteId) : '',
      priority: 'Medium',
      count: 20,
      assigned_team: isMember ? currentTeam : '',
      estimated_hours: 0,
      estimated_minutes: 0,
    },
  });

  const selectedTaskType = watch('task_type');

  const handleFormSubmit = (data: any) => {
    if (!data.due_date) {
      toast.error('Due date is required');
      return;
    }

    const pageTypeId = data.page_type_id ? parseInt(data.page_type_id) : null;
    const estimatedHours = parseInt(data.estimated_hours) || 0;
    const estimatedMinutes = parseInt(data.estimated_minutes) || 0;
    if (estimatedHours * 60 + estimatedMinutes <= 0) {
      toast.error('Time estimate is required');
      return;
    }
    const customTaskType = String(data.custom_task_type || '').trim();
    if (data.task_type === 'Other' && !customTaskType) {
      toast.error('Please enter custom task type');
      return;
    }

    const payload: any = {
      website_id: data.website_id ? parseInt(data.website_id) : null,
      page_type_id: pageTypeId,
      task_type: data.task_type === 'Other' ? customTaskType : data.task_type,
      assigned_to: selectedAssignees.join(', ') || null,
      assigned_to_list: selectedAssignees,
      assigned_team: getTeamsForAssignees(selectedAssignees, members, data.assigned_team || ''),
      follow_up_by: followUpBy || null,
      priority: data.priority,
      due_date: data.due_date || null,
      estimated_hours: estimatedHours,
      estimated_minutes: estimatedMinutes,
    };
    if (data.page_urls && data.page_urls.trim()) {
      payload.page_urls = data.page_urls.split('\n').map((u: string) => u.trim()).filter(Boolean);
    } else {
      payload.count = parseInt(data.count) || 20;
    }
    onSubmit(payload);
  };

  const pageTypeOptions = [
    { value: '', label: 'Other / General' },
    ...pageTypes.map((p: any) => ({ value: String(p.id), label: `${p.name} (${p.total_pages} pages)` })),
  ];
  const websiteOptions = [
    { value: '', label: 'Select website' },
    ...websites.map((site: any) => ({ value: String(site.id), label: site.name })),
  ];
  const taskTypeOptions = [
    { value: '', label: 'Select task type' },
    ...TASK_TYPES.map(t => ({ value: t, label: t })),
  ];
  const teamOptions = [
    { value: '', label: 'Select team' },
    ...TEAMS.map(t => ({ value: t, label: t })),
  ];

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm text-orange-700">
        💡 Create the same task type for multiple pages at once. Paste URLs (one per line) or just enter a count.
      </div>

      <Select label="Website *" {...register('website_id', { required: true })} options={websiteOptions} />

      <div className="grid grid-cols-2 gap-4">
        <Select label="Page Type" {...register('page_type_id')} options={pageTypeOptions} />
        <Select label="Task Type *" {...register('task_type', { required: true })} options={taskTypeOptions} />
      </div>
      {selectedTaskType === 'Other' && (
        <Input
          label="Custom Task Type *"
          {...register('custom_task_type')}
          placeholder="e.g. Bulk footer links cleanup"
        />
      )}

      <Textarea
        label="Page URLs (one per line) — leave empty to use count below"
        {...register('page_urls')}
        rows={4}
        placeholder={"https://tkxel.com/services/web-development\nhttps://tkxel.com/services/mobile-app"}
        className="font-mono"
      />

      <Input label="Count (used if no URLs provided)" type="number" {...register('count')} min={1} max={500} />

      <div className="grid grid-cols-2 gap-4">
        <Select label="Team" {...register('assigned_team')} options={teamOptions} />
        <FollowUpOwnerSelect members={members} value={followUpBy} onChange={setFollowUpBy} />
      </div>

      <MultiAssigneePicker
        label="Assigned To"
        members={members}
        value={selectedAssignees}
        onChange={assignees => {
          setSelectedAssignees(assignees);
          const team = getTeamsForAssignees(assignees, members);
          if (team) setValue('assigned_team', team);
        }}
      />

      <div className="grid grid-cols-2 gap-4">
        <Select label="Priority" {...register('priority')} options={PRIORITIES.map(p => ({ value: p, label: p }))} />
        <Input label="Due Date *" type="date" {...register('due_date')} />
      </div>

      <div>
        <label className="block text-sm font-semibold text-[#18181B] mb-1.5">Time Estimate *</label>
        <div className="flex gap-2">
          <select {...register('estimated_hours')}
            className="flex-1 border border-[#E4E4E7] rounded-lg px-3 py-2 text-sm text-[#18181B] focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#F97316] bg-white transition-all duration-150">
            {HOURS.map(h => <option key={h} value={h}>{h}h</option>)}
          </select>
          <select {...register('estimated_minutes')}
            className="flex-1 border border-[#E4E4E7] rounded-lg px-3 py-2 text-sm text-[#18181B] focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#F97316] bg-white transition-all duration-150">
            {MINUTES.map(m => <option key={m} value={m}>{m}m</option>)}
          </select>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <Button type="submit" loading={loading}>Create Batch</Button>
      </div>
    </form>
  );
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{label}</p>
      <p className="mt-1 text-sm font-medium text-gray-800 break-words">{value || '—'}</p>
    </div>
  );
}

// Inline hours/minutes editor — lets a member adjust the time estimate on their own
// task without opening the full edit form (which stays admin-only).
function EstimateEditor({ task, onSave, saving }: { task: any; onSave: (hours: number, minutes: number) => void; saving?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [hours, setHours] = useState<number>(task.estimated_hours || 0);
  const [minutes, setMinutes] = useState<number>(task.estimated_minutes || 0);

  useEffect(() => {
    setHours(task.estimated_hours || 0);
    setMinutes(task.estimated_minutes || 0);
  }, [task.estimated_hours, task.estimated_minutes]);

  const estimate = (task.estimated_hours > 0 || task.estimated_minutes > 0)
    ? `${task.estimated_hours > 0 ? task.estimated_hours + 'h ' : ''}${task.estimated_minutes > 0 ? task.estimated_minutes + 'm' : ''}`.trim()
    : null;

  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <input type="number" min={0} max={999} value={hours}
          onChange={e => setHours(Math.max(0, Math.min(999, Number(e.target.value))))}
          className="w-14 border border-gray-300 rounded px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400" />
        <span className="text-xs text-gray-400">h</span>
        <input type="number" min={0} max={59} value={minutes}
          onChange={e => setMinutes(Math.max(0, Math.min(59, Number(e.target.value))))}
          className="w-14 border border-gray-300 rounded px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400" />
        <span className="text-xs text-gray-400">m</span>
        <button onClick={() => { onSave(hours, minutes); setEditing(false); }} disabled={saving}
          className="text-xs font-semibold text-green-600 hover:underline ml-1">Save</button>
        <button onClick={() => { setEditing(false); setHours(task.estimated_hours || 0); setMinutes(task.estimated_minutes || 0); }}
          className="text-xs text-gray-400 hover:underline">Cancel</button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span>{estimate || '—'}</span>
      <button onClick={() => setEditing(true)} className="p-0.5 text-gray-400 hover:text-blue-500" title="Edit time">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
      </button>
    </div>
  );
}

// Read-only task view — members use this instead of opening the edit form just to see details.
// Time estimate stays editable for whoever the task is visible to (their own task).
function TaskDetailView({ task, onSaveEstimate, savingEstimate }: { task: any; onSaveEstimate: (hours: number, minutes: number) => void; savingEstimate?: boolean }) {
  const overdueDays = getOverdueDays(task.due_date, task.status);
  const pageUrl = normalizeUrlForWebsite(task.page_url, task.website_url);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={task.status} />
        <PriorityBadge priority={task.priority} />
        {overdueDays && <OverdueBadge days={overdueDays} />}
        {task.assigned_team && <TeamBadge team={task.assigned_team} />}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <DetailField label="Website" value={task.website_name} />
        <DetailField label="Page Type" value={task.page_type_name} />
        <DetailField label="Task Type" value={task.task_type} />
        <DetailField label="Assigned To" value={formatAssigneeNames(task)} />
        <DetailField label="Follow-up" value={task.follow_up_by} />
        <DetailField label="Due Date" value={formatDate(task.due_date)} />
        <DetailField label="Time Estimate" value={<EstimateEditor task={task} onSave={onSaveEstimate} saving={savingEstimate} />} />
        <DetailField label="Created By" value={task.created_by} />
      </div>

      {pageUrl && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Page URL</p>
          <a href={pageUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">{pageUrl}</a>
        </div>
      )}

      {task.notes && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">Notes</p>
          <FormattedNotes value={task.notes} />
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function PageTasks() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isMember = (user as any)?.role === 'member';
  const isAdmin = (user as any)?.role === 'admin';
  const currentMemberName = ((user as any)?.team_member_name || user?.username || '').trim();
  const currentUsername = (user?.username || '').trim();
  const [selectedPageType, setSelectedPageType] = useState<number | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);
  const [editTask, setEditTask] = useState<any>(null);
  const [viewTask, setViewTask] = useState<any>(null);
  const [deleteTask, setDeleteTask] = useState<any>(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const { data: pageTypesData } = useQuery({ queryKey: ['page-types'], queryFn: pageTypesApi.list });
  const { data: membersData } = useQuery({ queryKey: ['settings', 'team-members'], queryFn: settingsApi.teamMembers.list });
  const { data: websitesData } = useQuery({ queryKey: ['settings', 'websites'], queryFn: settingsApi.websites.list });

  const pageTypes = pageTypesData?.data || [];
  const members = membersData?.data || [];
  const websites = websitesData?.data || [];

  const params: Record<string, string> = {};
  if (selectedPageType) params.page_type_id = String(selectedPageType);
  if (filters.team) params.team = filters.team;
  params.status = filters.status || 'open';
  if (filters.priority) params.priority = filters.priority;
  if (filters.website_id) params.website_id = filters.website_id;
  if (filters.assigned_to) params.assigned_to = filters.assigned_to;
  if (search) params.search = search;

  const { data, isLoading } = useQuery({
    queryKey: ['page-tasks', params],
    queryFn: () => pageTasksApi.list(params),
  });

  const createMutation = useMutation({
    mutationFn: pageTasksApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['page-tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setAddOpen(false);
      toast.success('Task created!');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to create task'),
  });

  const batchMutation = useMutation({
    mutationFn: pageTasksApi.createBatch,
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['page-tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setBatchOpen(false);
      toast.success(`✅ Created ${data.created} tasks`);
    },
    onError: (err: any) => toast.error(err.message || 'Batch failed'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => pageTasksApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['page-tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setEditTask(null);
      toast.success('Task updated!');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const estimateMutation = useMutation({
    mutationFn: ({ id, estimated_hours, estimated_minutes }: { id: number; estimated_hours: number; estimated_minutes: number }) =>
      pageTasksApi.update(id, { estimated_hours, estimated_minutes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['page-tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success('Time estimate updated');
    },
    onError: (err: any) => toast.error(err.message || 'Failed to update time estimate'),
  });

  const deleteMutation = useMutation({
    mutationFn: pageTasksApi.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['page-tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setDeleteTask(null);
      toast.success('Task deleted');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: pageTasksApi.bulkDelete,
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['page-tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setSelectedTaskIds([]);
      setBulkDeleteOpen(false);
      toast.success(`Deleted ${data.deleted ?? 'selected'} tasks`);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => pageTasksApi.update(id, { status }),
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: ['page-tasks', params] });
      const prev = qc.getQueryData(['page-tasks', params]);
      qc.setQueryData(['page-tasks', params], (old: any) => ({
        ...old,
        data: old?.data?.map((t: any) => t.id === id ? { ...t, status } : t),
      }));
      return { prev };
    },
    onError: (_e: any, _v: any, ctx: any) => {
      qc.setQueryData(['page-tasks', params], ctx?.prev);
      toast.error('Status update failed');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['page-tasks'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  const tasks = (data?.data || []).filter((task: any) =>
    !isMember ||
    memberCanSeeTask(task, currentMemberName, currentUsername)
  );
  const total = isMember ? tasks.length : data?.total || 0;
  const visibleTaskIds = tasks.map((task: any) => Number(task.id));
  const allVisibleSelected = visibleTaskIds.length > 0 && visibleTaskIds.every((id: number) => selectedTaskIds.includes(id));

  React.useEffect(() => {
    setSelectedTaskIds(selected => selected.filter((id: number) => visibleTaskIds.includes(id)));
  }, [visibleTaskIds.join(',')]);

  const toggleTaskSelection = (id: number) => {
    setSelectedTaskIds(selected =>
      selected.includes(id) ? selected.filter(selectedId => selectedId !== id) : [...selected, id]
    );
  };

  const toggleAllVisible = () => {
    setSelectedTaskIds(allVisibleSelected ? [] : visibleTaskIds);
  };

  return (
    <div className="flex gap-5 h-full">
      {/* Sidebar */}
      <div className="w-56 flex-shrink-0">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden sticky top-0">
          <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Page Types</p>
          </div>
          <ul className="py-1">
            <li>
              <button
                onClick={() => setSelectedPageType(null)}
                className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${selectedPageType === null ? 'bg-orange-50 text-orange-700 font-semibold border-l-2 border-orange-500' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                All Pages
              </button>
            </li>
            {pageTypes.map((pt: any) => (
              <li key={pt.id}>
                <button
                  onClick={() => setSelectedPageType(pt.id)}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors ${selectedPageType === pt.id ? 'bg-orange-50 text-orange-700 font-semibold border-l-2 border-orange-500' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="truncate">{pt.name}</span>
                    <span className="text-xs text-gray-400 ml-1 flex-shrink-0">{pt.total_pages}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 space-y-4 min-w-0">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">{total} tasks</p>
          <div className="flex gap-2">
            {isAdmin && selectedTaskIds.length > 0 && (
              <Button variant="danger" onClick={() => setBulkDeleteOpen(true)}>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete selected ({selectedTaskIds.length})
              </Button>
            )}
            <Button variant="outline" onClick={() => setBatchOpen(true)}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
              Batch Create
            </Button>
            <Button onClick={() => setAddOpen(true)}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Task
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-wrap gap-3">
          <input type="text" placeholder="Search tasks..." value={search} onChange={e => setSearch(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1 min-w-36 focus:outline-none focus:ring-2 focus:ring-orange-400" />
          <select value={filters.website_id || ''} onChange={e => setFilters(f => ({ ...f, website_id: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
            <option value="">All Websites</option>
            {websites.map((site: any) => <option key={site.id} value={site.id}>{site.name}</option>)}
          </select>
          <select value={filters.team || ''} onChange={e => setFilters(f => ({ ...f, team: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
            <option value="">All Teams</option>
            {TEAMS.map(t => <option key={t}>{t}</option>)}
          </select>
          <select value={filters.assigned_to || ''} onChange={e => setFilters(f => ({ ...f, assigned_to: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
            <option value="">All Members</option>
            {members.map((member: any) => (
              <option key={member.id} value={member.name}>{member.name} ({member.team})</option>
            ))}
          </select>
          <select
            value={filters.status || 'open'}
            onChange={e => setFilters(f => ({ ...f, status: e.target.value === 'open' ? '' : e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
          >
            {STATUS_FILTERS.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <select value={filters.priority || ''} onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400">
            <option value="">All Priorities</option>
            {PRIORITIES.map(p => <option key={p}>{p}</option>)}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {isLoading ? <TableSkeleton rows={8} cols={7} /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {isAdmin && (
                      <th className="w-10 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={allVisibleSelected}
                          onChange={toggleAllVisible}
                          className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                          aria-label="Select all visible page tasks"
                        />
                      </th>
                    )}
                    {['#', 'Website', 'Page Type', 'URL', 'Task Type', 'Assigned', 'Priority', 'Status', 'Due', 'Estimate', 'Actions'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task: any) => (
                    <tr key={task.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      {isAdmin && (
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedTaskIds.includes(Number(task.id))}
                            onChange={() => toggleTaskSelection(Number(task.id))}
                            className="h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                            aria-label={`Select page task ${task.id}`}
                          />
                        </td>
                      )}
                      <td className="px-4 py-3 text-gray-400 font-mono text-xs">{task.id}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-semibold">
                          {task.website_name || 'Tkxel'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">{task.page_type_name || 'Other / General'}</span>
                      </td>
                      <td className="px-4 py-3">
                        {task.page_url ? (
                          <a href={normalizeUrlForWebsite(task.page_url, task.website_url)}
                            target="_blank" rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline font-mono truncate max-w-[180px] block">
                            {task.page_url}
                          </a>
                        ) : <span className="text-gray-300 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-700 text-xs max-w-[220px]">
                        <p className="font-medium">{task.task_type || '—'}</p>
                        {task.notes && (
                          <FormattedNotes value={task.notes} compact tone="muted" className="mt-1" />
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {formatAssigneeNames(task)
                          ? (
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-gray-700">{formatAssigneeNames(task)}</p>
                              {task.assigned_team && <TeamBadge team={task.assigned_team} />}
                              {task.follow_up_by && (
                                <p className="text-[11px] text-gray-400">Follow-up: {task.follow_up_by}</p>
                              )}
                            </div>
                          )
                          : (
                            <div className="space-y-1">
                              <span className="text-gray-300 text-xs">—</span>
                              {task.follow_up_by && (
                                <p className="text-[11px] text-gray-400">Follow-up: {task.follow_up_by}</p>
                              )}
                            </div>
                          )}
                      </td>
                      <td className="px-4 py-3"><PriorityBadge priority={task.priority} /></td>
                      <td className="px-4 py-3">
                        <select value={task.status} onChange={e => statusMutation.mutate({ id: task.id, status: e.target.value })}
                          className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-orange-400">
                          {STATUSES.map(s => <option key={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(task.due_date)}</td>
                      <td className="px-4 py-3 text-xs font-medium text-gray-700 whitespace-nowrap">
                        <EstimateEditor task={task}
                          onSave={(hours, minutes) => estimateMutation.mutate({ id: task.id, estimated_hours: hours, estimated_minutes: minutes })}
                          saving={estimateMutation.isPending} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button onClick={() => setViewTask(task)} className="p-1.5 hover:bg-gray-100 rounded text-gray-500" title="View details">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                          </button>
                          {isAdmin && (
                            <button onClick={() => setEditTask(task)} className="p-1.5 hover:bg-blue-50 rounded text-blue-500" title="Edit">
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                            </button>
                          )}
                          {isAdmin && (
                            <button onClick={() => setDeleteTask(task)} className="p-1.5 hover:bg-red-50 rounded text-red-400" title="Delete">
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {tasks.length === 0 && (
                    <tr><td colSpan={isAdmin ? 12 : 11} className="px-4 py-12 text-center text-gray-400">No page tasks yet. Use "Batch Create" to add tasks in bulk.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add Page Task" size="lg">
        <TaskForm onSubmit={(d: any) => createMutation.mutate(d)} loading={createMutation.isPending}
          submitLabel="Create Task" pageTypes={pageTypes} members={members} websites={websites} />
      </Modal>

      <Modal open={batchOpen} onClose={() => setBatchOpen(false)} title="Batch Create Page Tasks" size="lg">
        <BatchForm onSubmit={(d: any) => batchMutation.mutate(d)} loading={batchMutation.isPending}
          pageTypes={pageTypes} members={members} websites={websites} />
      </Modal>

      <Modal open={!!editTask} onClose={() => setEditTask(null)} title="Edit Page Task" size="lg">
        {editTask && (
          <TaskForm defaultValues={editTask}
            onSubmit={(d: any) => updateMutation.mutate({ id: editTask.id, data: d })}
            loading={updateMutation.isPending} submitLabel="Save Changes"
            pageTypes={pageTypes} members={members} websites={websites} />
        )}
      </Modal>

      <Modal open={!!viewTask} onClose={() => setViewTask(null)} title="Task Details" size="lg">
        {viewTask && (
          <TaskDetailView
            task={tasks.find((t: any) => t.id === viewTask.id) || viewTask}
            onSaveEstimate={(hours, minutes) => estimateMutation.mutate({ id: viewTask.id, estimated_hours: hours, estimated_minutes: minutes })}
            savingEstimate={estimateMutation.isPending}
          />
        )}
      </Modal>

      <ConfirmDialog open={!!deleteTask} onClose={() => setDeleteTask(null)}
        onConfirm={() => deleteMutation.mutate(deleteTask?.id)} title="Delete Task"
        message="Are you sure you want to delete this task? This cannot be undone."
        loading={deleteMutation.isPending} />
      <ConfirmDialog open={bulkDeleteOpen} onClose={() => setBulkDeleteOpen(false)}
        onConfirm={() => bulkDeleteMutation.mutate(selectedTaskIds)} title="Delete Selected Tasks"
        message={`Are you sure you want to delete ${selectedTaskIds.length} selected page task${selectedTaskIds.length === 1 ? '' : 's'}? This cannot be undone.`}
        loading={bulkDeleteMutation.isPending} />
    </div>
  );
}
